import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";

import { Shell } from "./components/Shell";
import { SignIn } from "./components/SignIn";
import { CalendarPage } from "./pages/CalendarPage";
import { Dashboard } from "./pages/Dashboard";
import { MeetingDetail } from "./pages/MeetingDetail";
import { Meetings } from "./pages/Meetings";
import { Personalization } from "./pages/Personalization";
import { ProjectPage } from "./pages/ProjectPage";
import { ProjectsPage } from "./pages/ProjectsPage";
import { RecordPage } from "./pages/RecordPage";
import { UploadPage } from "./pages/UploadPage";
import { NotSignedIn, endpoints } from "./lib/api";
import "./styles/app.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Sign-in is the backend's job; retrying a 401 only delays the redirect.
      retry: (failureCount, error) => !(error instanceof NotSignedIn) && failureCount < 2,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  const { data: me, error, isLoading } = useQuery({ queryKey: ["me"], queryFn: endpoints.me, retry: false });

  if (isLoading) return <div className="empty" style={{ padding: 40 }}>Loading…</div>;

  if (error instanceof NotSignedIn) return <SignIn />;

  return (
    <Routes>
      <Route element={<Shell me={me ?? null} />}>
        <Route index element={<Dashboard me={me ?? null} />} />
        <Route path="meetings" element={<Meetings />} />
        <Route path="meetings/:sessionId" element={<MeetingDetail />} />
        <Route path="projects" element={<ProjectsPage />} />
        <Route path="projects/:projectId" element={<ProjectPage />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="record" element={<RecordPage me={me ?? null} />} />
        <Route path="upload" element={<UploadPage me={me ?? null} />} />
        <Route path="personalization" element={<Personalization />} />
        <Route path="*" element={<div className="page-body">Page not found.</div>} />
      </Route>
    </Routes>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
